import { useClose } from '@headlessui/react';
import SanityImage from '@otovo/shared/components/Sanity/SanityImage';
import { localeToMarket } from '@otovo/shared/lib/locale';
import useMobileVersion from '@otovo/shared/lib/useMobileVersion';
import { Otovo$Locale } from '@otovo/shared/types/otovoweb';
import { Sanity$Image } from '@otovo/shared/types/sanityImage';
import { Sanity$NavbarSection } from '@otovo/shared/types/sanityTypes';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import MenuHeading from '../FlyOutMenu/MenuHeading';
import MenuLink from '../FlyOutMenu/MenuLink';
import { getNavLinkUrl, getOptionalProps } from './navUtils';

type Props = {
  mainSection: Sanity$NavbarSection;
  subSection?: Sanity$NavbarSection;
  image: Sanity$Image;
};

const DropdownContent = ({ mainSection, subSection, image }: Props) => {
  const closeMenu = useClose();
  const isTablet = useMobileVersion(768);

  const router = useRouter();
  const locale = router.locale as Otovo$Locale;
  const market = localeToMarket(locale);

  useEffect(() => {
    const handleRouteChange = () => {
      closeMenu();
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [closeMenu, router.events]);

  return (
    <div className="mx-4 grid max-w-8xl grid-cols-12 gap-6 md:mx-16 2xl:mx-auto">
      {/* Packages section */}
      <div className="col-span-3 col-start-1 lg:col-start-2">
        <MenuHeading>{mainSection.heading}</MenuHeading>
        <ul className="lg:space-y-4">
          {mainSection.pages?.map((link) => {
            return (
              <li key={link._key}>
                <MenuLink
                  size={isTablet ? 'small' : 'big'}
                  href={getNavLinkUrl(link, market, locale)}
                  {...getOptionalProps(link)}
                >
                  {link.label}
                </MenuLink>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Product section */}
      <div className="col-span-3 col-start-4 lg:col-start-5">
        <MenuHeading>{subSection.heading}</MenuHeading>
        <ul className="lg:space-y-3">
          {subSection.pages?.map((link) => {
            return (
              <li key={link._key}>
                <MenuLink
                  size="small"
                  href={getNavLinkUrl(link, market, locale)}
                  {...getOptionalProps(link)}
                >
                  {link.label}
                </MenuLink>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Image */}
      <div className="col-span-6 col-start-7 lg:col-span-5 lg:col-start-8">
        <div className="overflow-hidden">
          <SanityImage
            image={image}
            className="aspect-3/2 h-full w-full rounded-md object-cover"
            placeholder="blur"
            blurDataURL={image.asset.metadata.lqip}
            // Do not load image on mobile
            sizes="(max-width: 639px) 0px, 586px"
            maxHeightFull
          />
        </div>
      </div>
    </div>
  );
};

export default DropdownContent;
